<template>
  <page-layout
    title="Assignment Board"
    meta-title="Assignment Board"
    :loaded="loaded"
  >
    <v-layout
      class="room-assignment-board"
      :class="{ 'is-mobile': isMobile }"
      align-stretch
    >
      <div class="content-main">
        <board-canvas
          ref="boardCanvas"
          :columns="participantStatus"
          :items="guestsList"
          :controls="guestControls"
          sort
          hideAddButton
          groupByField="assignment_status"
          @reorderItems="onItemsChangeOrder"
          @click.native="handleBoardClick"
        />
      </div>
    </v-layout>

    <room-assignment-invite
      ref="roomAssignmentInvite"
      :selected="invitedParticipants"
      :participants="participants"
      @sendActionToParticipant="sendActionToParticipant"
    />
  </page-layout>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

const Ably = require('ably');
import { vueEnv } from '@/globals';
import Colors from '@/views/reports/components/funnel/colorsGenerator';
import { eventBus } from '@/lib/events';
import { extractAssignmentsState } from '@/components/rooms/helpers/boardAssignment';

import BoardCanvas from '@/components/board/BoardCanvas.vue';
import RoomAssignmentInvite from '@/components/rooms/components/roomAssignmentBoard/RoomAssignmentInvite.vue';

export default {
  name: 'RoomAssignmentBoard',
  components: {
    BoardCanvas,
    RoomAssignmentInvite,
  },
  data() {
    return {
      ablyChannel: null,
      ablyGuestChannel: null,
      participants: [],
      droppedParticipants: [],
      selectedParticipants: [],
      invitedParticipants: [],
      loaded: false,
      guestControls: [
        {
          title: 'Invite',
          action: (item) => {
            this.openInviteModal(item.id);
          },
        },
      ],
    };
  },
  computed: {
    ...mapState('rooms', ['roomInfo']),
    participantStatus() {
      const hostsAsStatus = this.hostsList
        .map((host, index) => {
          return {
            key: host.id,
            title: host.text || host.id || '',
            color: Colors.getColorByIndex(index),
          };
        })
        .sort((a, b) => {
          return a.title.localeCompare(b.title);
        });

      return [
        {
          key: null,
          title: 'Unassigned',
          color: '#909090',
        },
        ...hostsAsStatus,
        {
          key: 'rejected',
          title: 'Rejected',
          color: '#004e75',
        },
      ];
    },

    hostsList() {
      return this.participants.filter((p) => p.type === 'host');
    },

    guestsList() {
      return this.participants.filter((p) => p.type === 'guest');
    },

    roomKey() {
      return this.$route?.query?.room;
    },

    hostId() {
      return this.$route?.query?.hostId;
    },

    currentUser() {
      return this.hostsList.find((host) => host.id === this.hostId);
    },
  },

  watch: {
    currentUser(user) {
      if (!user) return;

      const { query } = this.$route || {};
      const token = localStorage.getItem(`apex-${query.hostId}-token`);

      this.setRoomInfo({
        ...query,
        name: user.text,
        identity: query.hostId,
        avatar: user.avatar,
        type: 'host',
        token,
      });
    },
  },

  methods: {
    ...mapMutations('rooms', ['setRoomInfo']),

    openInviteModal(identity) {
      if (!this.$refs.roomAssignmentInvite || !identity) return;

      this.invitedParticipants = [identity];
      this.$refs.roomAssignmentInvite.openInviteModal();
    },

    handleBoardClick() {
      const selectedCards = document.querySelectorAll('.board-card.selected');

      this.selectedParticipants = [];
      selectedCards.forEach((el) => {
        if (el.id) {
          this.selectedParticipants.push(el.id);
        }
      });
    },

    isStatusExists({ status }) {
      return this.participantStatus.some(({ key }) => key === status);
    },

    setupAblyChannel() {
      const ablyClient = new Ably.Realtime(vueEnv('ably_key'));
      ablyClient.connection.on('connected', async () => {
        const channelName = `[?rewind=120m]vroom:${this.roomKey}`;

        this.ablyChannel = ablyClient.channels.get(channelName);

        this.ablyGuestChannel = ablyClient.channels.get(`${channelName}guest`);

        // extract chat history
        const now = Date.now();
        const messagesHistory = await this.ablyChannel.history({
          end: now,
          limit: 1000,
        });
        this.extractParticipantsFrom(messagesHistory.items || []);

        await this.extractParticipantStateChangesFrom(
          messagesHistory.items || []
        );

        // add listeners only for new messages
        this.ablyChannel.subscribe('presence', (message) => {
          const isOutdated = new Date(message.data?.date) < new Date(now);
          if (isOutdated) return;

          this.extractSingleParticipantFrom(message?.data);
        });

        this.ablyChannel.subscribe('participantStateChanged', (message) => {
          const isOutdated = new Date(message.data?.timestamp) < new Date(now);
          if (isOutdated) return;

          this.handleParticipantStateChange(message?.data);
        });

        this.loaded = true;
      });
    },

    extractParticipantsFrom(messages) {
      const lastMessages = messages
        .filter(({ name }) => name === 'presence')
        .reduce((acc, message) => {
          const { identity, date } = message.data || {};
          if (!identity) return acc;

          const messageDate = new Date(date);

          if (!acc[identity] || messageDate > new Date(acc[identity].date)) {
            acc[identity] = message.data;
          }

          return acc;
        }, {});

      // Extract records whose last action is 'joined'
      Object.values(lastMessages).forEach((message) => {
        if (message.action === 'joined' || message.type === 'host') {
          this.extractSingleParticipantFrom(message);
        }
      });
    },

    async extractParticipantStateChangesFrom(messages) {
      await this.$nextTick();

      const lastColumnChanges = extractAssignmentsState(messages);

      if (!lastColumnChanges) return;

      Object.values(lastColumnChanges)
        .filter(this.isStatusExists)
        .forEach(({ participants, status }) => {
          // update local participants and participants in main room
          this.sendParticipantStateChange(participants, status);

          // reorder items in board
          eventBus.trigger('updateItemsOrder', {
            key: status,
            items: participants.map((p) => p.identity),
          });
        });
    },

    // refresh column order when participant leaves room
    async refreshColumnOrder(columnKey) {
      await this.$nextTick();

      if (columnKey === null) return;

      const column = document.getElementById(columnKey);
      if (!column) return;

      const items = column.querySelectorAll('.board-card');
      if (!items.length) return;

      const itemsList = [];

      items.forEach((el) => {
        itemsList.push(el.id);
      });

      this.onItemsChangeOrder({
        key: columnKey,
        items: itemsList,
      });
    },

    dropParticipant(participant) {
      if (!participant || participant.type === 'host') return;

      const pIndex = this.droppedParticipants.findIndex((p) => {
        return p.id === participant.id;
      });

      if (pIndex === -1) {
        this.droppedParticipants.push(participant);
      } else {
        this.droppedParticipants.splice(pIndex, 1);
      }

      this.participants = this.participants.filter((p) => {
        return p.id !== participant.id;
      });

      this.refreshColumnOrder(participant.assignment_status);
    },

    addParticipant(participant) {
      const isAlreadyAdded = this.participants.some((p) => {
        return p.id === participant.identity;
      });

      if (isAlreadyAdded) return;

      const droppedParticipant = this.droppedParticipants.find((p) => {
        return p.id === participant.identity;
      });

      this.participants.push({
        id: participant.identity,
        title: participant.identity,
        text: participant.name,

        name: participant.name,
        identity: participant.identity,
        avatar: participant.avatar,
        type: participant.type,
        assignment_order: droppedParticipant?.assignment_order || 0,
        assignment_status:
          participant.assignment_status ||
          droppedParticipant?.assignment_status ||
          null,
      });

      if (droppedParticipant?.assignment_status) {
        this.refreshColumnOrder(droppedParticipant.assignment_status);
      }
    },

    extractSingleParticipantFrom(message) {
      const { identity, action, type } = message;

      if (action === 'left' && type === 'guest') {
        const participant = this.participants.find((p) => p.id === identity);
        this.dropParticipant(participant);
      }

      if (action === 'joined' || type === 'host') {
        this.addParticipant(message);
      }
    },

    onItemsChangeOrder(e) {
      const { key, items } = e;
      const participantsUpdate = items.map((identity, index) => {
        return {
          identity,
          assignment_order: index + 1,
          assignment_status: key,
        };
      });

      this.sendParticipantStateChange(participantsUpdate, key);
    },

    async handleParticipantStateChange(updates) {
      const { from, participants, status } = updates;
      if (from === this.hostId) return;

      this.updateLocalParticipants(participants);

      await this.$nextTick();

      eventBus.trigger('updateItemsOrder', {
        key: status,
        items: participants.map((p) => p.identity),
      });
    },

    sendParticipantStateChange(participants = [], status) {
      this.ablyChannel.publish('participantStateChanged', {
        participants,
        from: this.hostId,
        status,
        timestamp: Date.now(),
      });

      this.updateLocalParticipants(participants);
    },

    async updateLocalParticipants(participants) {
      await this.$nextTick();

      const keysToCheck = ['assignment_order', 'assignment_status'];

      participants.forEach((participant) => {
        const localParticipant = this.participants.find((p) => {
          return p.id === participant.identity;
        });

        const hasChanges =
          localParticipant &&
          keysToCheck.some((key) => {
            return localParticipant[key] !== participant[key];
          });

        if (hasChanges) {
          localParticipant.assignment_order = participant.assignment_order;
          localParticipant.assignment_status = participant.assignment_status;
        }
      });
    },

    sendActionToParticipant(data) {
      this.ablyGuestChannel.publish('actionFromAssignmentBoard', data);

      if (data.info) {
        const { identity } = data.info;
        this.invitedParticipants.filter((id) => id !== identity);
      }
    },

    stopTracksForHost() {
      this.ablyGuestChannel.publish('actionFromAssignmentBoard', {
        stopTracks: true,
        to: {
          identity: this.hostId,
        },
      });
    },

    publishMessage(message) {
      this.ablyChannel.publish('message', message);
    },
  },
  mounted() {
    this.setupAblyChannel();
    eventBus.on('sendAssignmentChange', this.publishMessage);
    eventBus.on('stopTracks', this.stopTracksForHost);
  },
};
</script>

<style lang="less">
.room-assignment-board {
  height: 100%;

  .content-main {
    height: 100%;
  }

  .board-col {
    min-width: 220px;
    max-width: 220px;
  }

  .board-canvas {
    min-height: 100%;
  }
}
</style>
